import type { Hold, RouteLine } from './climb';
import type { RouteSetter } from './route-setter';

/**
 * One hold a detector found, as a **shape**. Its points are on a 0-1000 grid —
 * a detector does not know the photo's pixel size — and *not* in the fractions
 * a climb's own marks are stored in. The conversion between the two happens in
 * exactly one place: `gym-admin/components/scans/detected-holds.ts`.
 *
 * `score` is the detector's own confidence where it reports one. It is kept
 * because the filtering wants it, and is never shown.
 */
export interface DetectedHold {
  outline: Array<[number, number]>;
  score?: number;
  /**
   * The colour measured off the pixels inside the outline, where a detector
   * measured one. On the hold rather than only on its group, because the panel
   * widens a selection from one hold outwards — "what else on this wall is this
   * colour" is a question about holds.
   */
  hex?: string;
  lab?: [number, number, number];
  /** Which colour group the detector put it in. A proposal, never a row. */
  group?: number;
}

/**
 * A climb the detector is **offering** to make: a name, a colour and the holds
 * it would take. Nothing of this is in the database — it is drawn in the panel
 * from the scan, and only becomes a `ClimbScanColor` when a setter saves it.
 */
export interface ProposedGroup {
  name: string | null;
  hex_colors: string[];
  climb_name?: string | null;
  /** Indices into the scan's `detected_holds`. */
  holds: number[];
}

/** A rectangle on that same grid, with x/y at its top left corner. */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A detection's bounding rectangle. Derived, which is why nothing stores it. */
export function boundingBoxOf(outline: Array<[number, number]>): BoundingBox {
  const xs = outline.map(([x]) => x);
  const ys = outline.map(([, y]) => y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);

  return { x: left, y: top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
}

export interface GradingSystemSummary {
  id: string;
  name: string;
  grades: Array<{ id: string; name: string }>;
}

export interface ClimbScanLocation {
  id: string;
  name: string;
  slug: string;
  boulder_grading_system: GradingSystemSummary | null;
  route_grading_system: GradingSystemSummary | null;
}

export interface ClimbScanColorClimb {
  id: string;
  slug: string;
  name: string;
  official_status: string;
  grade_id?: string | null;
  grade_name?: string | null;
  zone?: { id: string; name: string } | null;
  route_setter?: RouteSetter | null;
  /**
   * What a saved climb gives back so its marks can be edited rather than
   * redrawn — the gym panel loads these straight into the image editor.
   */
  hold_color?: string | null;
  hold_color_hex?: string[] | null;
  description?: string | null;
  climb_exceptions?: string[];
  holds?: Hold[] | null;
  line?: RouteLine | null;
  /**
   * Whether those holds are a sequence — the order they are climbed in — and
   * whether that sequence says which hand takes each of them. The panel reopens
   * a saved circuit as one rather than as a set of circles.
   */
  circuit?: boolean;
  forced_hands?: boolean;
  /**
   * The climb's own picture, and **only** when it is a cut of the wall rather
   * than the wall itself — which is to say, when the panel cropped it on the way
   * up. Its marks are fractions of that cut, so it is what they have to be drawn
   * back on; a colour that kept the whole wall has none of this and is drawn on
   * the scan photo like every other.
   */
  cropped_image_url?: string | null;
}

export interface ClimbScanColor {
  id: string;
  climb_scan_id: string;
  color_name: string | null;
  hex_colors: string[];
  hold_count: number | null;
  /**
   * The holds the scan found, as shapes. The API also sends them as rectangles
   * (`bounding_boxes`) for one release, for clients older than this one; nothing
   * here reads that, and it goes with the column.
   */
  detected_holds: DetectedHold[];
  climb_name: string | null;
  status: 'pending' | 'draft' | 'completed';
  climb: ClimbScanColorClimb | null;
  created_at: string;
  updated_at: string;
}

/**
 * How hard to look, on a re-scan of a segmentation-detected wall. The only
 * lever that changes anything: the same pixels and the same parameters give
 * the same answer, so "it was wrong" has nothing to act on.
 */
export type ScanSensitivity = 'fewer' | 'normal' | 'more';

export const SCAN_SENSITIVITIES: ScanSensitivity[] = ['fewer', 'normal', 'more'];

export interface ClimbScanZone {
  id: string;
  name: string;
  slug: string;
  image_small_url: string | null;
}

export type ClimbScanRouteSetter = RouteSetter;

/** Whoever took the photo — an account, unlike a setter, who may be a guest. */
export interface ClimbScanUploader {
  id: number;
  name: string;
  username: string;
  avatar_url?: string | null;
}

/** A rectangle on a photo, in fractions of it: `x`/`y` its top left corner. */
export interface PhotoArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The last area of a wall a setter asked to be looked at again, for holds the
 * scan missed (`POST …/scan_area`). It only ever adds holds to the wall.
 */
export interface ClimbScanAreaScan {
  id: string;
  area: PhotoArea;
  status: 'pending' | 'done' | 'failed';
  /** Still being looked at: pending, and not given up on as dead. */
  running: boolean;
  /** How many holds it added, once done. */
  added?: number;
  error?: string;
  requested_at: string;
  finished_at?: string;
}

/**
 * Who looks at a wall: Gemini (named colours, rectangles), SAM 3 on Replicate,
 * or the RF-DETR hold model we run ourselves. The last two segment.
 */
export type ScanDetector = 'rfdetr' | 'sam' | 'gemini';

/**
 * How far a running scan has got, for the bar over a photo being read in the
 * climb editor (`ClimbScans::LiveScan`): the tiles the hold model has
 * answered out of the calls issued so far (`total` grows when a full tile is
 * quartered), then the pipeline after them, which has nothing to count.
 */
export type ScanProgress =
  | { stage: 'tiles'; done: number; total: number }
  | { stage: 'holds' };

export interface ClimbScan {
  id: string;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  /**
   * `processing` with nobody processing it: the worker died mid-run. The batch
   * picks it back up within the hour; until then it can be asked for again,
   * which a scan that is really running cannot.
   */
  stalled: boolean;
  /**
   * Failed because somebody pressed Stop (a board's photo, in the board panel),
   * not because anything went wrong: it can simply be asked for again.
   */
  stopped?: boolean;
  climb_type: 'boulder' | 'route';
  image_url: string | null;
  image_small_url: string | null;
  error_message: string | null;
  /**
   * A photo taken in the member app's climb editor and read there and then
   * (`ClimbScans::LiveScan`). `personal` when the photographer has no climb
   * role at the gym: theirs, never listed in the panel.
   */
  live?: boolean;
  personal?: boolean;
  /** How far the job has got, while it runs; null otherwise. */
  progress?: ScanProgress | null;
  /**
   * Which detector would look at this wall. The panel needs it for one thing:
   * a re-scan asks a segmentation model for **parameters** and a language
   * model for **words**, because SAM run twice on the same pixels with the same
   * parameters gives the same answer. The dialog has to know which to ask for.
   * `rfdetr` is the hold model we run ourselves: it segments too, so it is
   * asked for parameters like SAM.
   */
  detector: ScanDetector;
  /**
   * The segmentation model whose answer the wall holds, or null: a Gemini
   * answer, or none yet. The scan dialog starts on it.
   */
  answered_by?: 'rfdetr' | 'sam' | null;
  /**
   * Whether the scan dialog may offer SAM 3 instead of `detector`: the
   * precise look, slower and paid for. Only on a gym that has both models.
   */
  precise_scan?: boolean;
  /**
   * Whether "scan this area" can be offered. The model that answered the wall
   * looks at the area, so the gym needs one that traces outlines.
   */
  area_scannable?: boolean;
  /**
   * How hard the wall was read. At "fewer" the panel hides the holds the
   * detector was unsure of (see `detected-holds.ts#shownDetections`).
   */
  sensitivity?: ScanSensitivity | null;
  /**
   * The confidence under which a detected hold is hidden — on the panel and
   * in the member app's climb editor — chosen with the panel's slider. Null
   * hides nothing (but see `shownDetections` for a wall read at "fewer").
   */
  min_score?: number | null;
  /**
   * The detected holds a setter excluded by hand, as places in
   * `detected_holds`: hidden like those under `min_score`, and put back the
   * same way. Emptied by the server when the wall is scanned again.
   */
  excluded_holds?: number[];
  /** Every hold on the wall. The pool a climb is chosen from. */
  detected_holds: DetectedHold[];
  /** What the detector would make of them, offered rather than written down. */
  proposed_groups: ProposedGroup[];
  /** The last area asked to be looked at again; absent on a wall never asked. */
  area_scan?: ClimbScanAreaScan | null;
  /** The climbs somebody has actually saved off this wall. */
  colors: ClimbScanColor[];
  location: ClimbScanLocation | null;
  zone: ClimbScanZone | null;
  /** Who uploaded it. Null only for scans whose user record has gone away. */
  user: ClimbScanUploader | null;
  route_setters: ClimbScanRouteSetter[];
  created_at: string;
  updated_at: string;
}

/**
 * A scanned wall, as the climb editor offers it beside the camera — see
 * Api::Locations::RecentClimbScansController.
 *
 * **Its holds are not here.** A dozen walls of traced outlines is far more than
 * a row of thumbnails needs, so they are read when one is picked
 * (`climbScansService.getScan`). What is here is how many there are: none means
 * a photograph and nothing more, either because the wall has not been looked at
 * yet or because nothing was found on it.
 */
export interface RecentClimbScan {
  scan_id: string;
  /** The sector the scan was filed under. It travels with the picture. */
  zone_id: string | null;
  status: ClimbScan['status'];
  holds_count: number;
  created_at: string | null;
  url: string | null;
  thumbnail_url: string | null;
}

/**
 * A scan is done with once every colour it turned up has become a confirmed
 * climb — nothing is left on it to set. The gym panel greys those out rather
 * than hiding them, so a wall reset stays readable after the fact.
 */
export function isScanArchived(scan: ClimbScan): boolean {
  if (scan.status !== 'ready') return false;

  return scan.colors.length > 0 && scan.colors.every((color) => color.status === 'completed');
}

/**
 * Whether a detector has ever answered about this wall.
 *
 * **Not the same question as "has a climb been saved off it", and reading the
 * rows for it was wrong.** The holds and the groups belong to the photograph
 * and are written down by the analysis itself; a `ClimbScanColor` appears only
 * once a setter accepts one of the proposals. So a wall with ninety-five holds
 * on it and no rows *has* been looked at — and the panel that counted rows
 * instead called it fresh, named the press "scan this wall", and started the
 * analysis without asking, which is the one case where there was most to say
 * about the last answer.
 */
export function hasScanAnswer(scan: ClimbScan): boolean {
  return scan.colors.length > 0 || scan.detected_holds.length > 0;
}

export interface SaveColorDraftData {
  name?: string | null;
  grade_id?: string | null;
  route_setter_id?: string | null;
  zone_id?: string | null;
  color_name?: string | null;
}

/**
 * Everything the gym panel decides about one of a scan's climbs in a single
 * save: the fields that identify it, and the marks drawn on the scan photo.
 */
export interface SaveScanClimbData {
  name: string;
  grade_id: string;
  zone_id?: string | null;
  route_setter_id?: string | null;
  /** The colour of the holds, as the gym names it (e.g. "red"). */
  color_name?: string | null;
  description?: string;
  climb_exceptions?: string[];
  holds?: Hold[];
  line?: RouteLine | null;
  /** Whether those holds are a sequence, and whether it names the hands. */
  circuit?: boolean;
  forced_hands?: boolean;
  /**
   * The photo cut down to what the setter had on screen, where they asked for
   * that. Absent otherwise, and then the climb keeps the copy of the wall it was
   * given when its draft was made.
   */
  image_signed_id?: string;
}

export interface PublishDraftData {
  image_signed_id?: string;
  /** The whole photograph, when the one above is a crop of it. */
  original_image_signed_id?: string;
  /**
   * That the picture above *is* a crop, for a caller with no whole photograph to
   * send: a scan's draft is already holding the wall it was cut out of, and the
   * endpoint keeps that as the original rather than have it uploaded twice.
   */
  cropped?: boolean;
  name?: string;
  grade_id?: string;
  zone_id?: string | null;
  route_setter_id?: string | null;
  hold_color?: string;
  description?: string;
  climb_exceptions?: string[];
  holds?: Hold[];
  line?: RouteLine | null;
  /** Whether those holds are a sequence, and whether it names the hands. */
  circuit?: boolean;
  forced_hands?: boolean;
}
